// libraries
import hotkeys from 'hotkeys-js';

// components
import type { ButtonProps } from '@interstate/components/Button';
import { Button } from '@interstate/components/Button';
import { ChatBubbleLeftRightIcon, ChevronDownIcon } from '@interstate/components/Icons';
import { Snackbar } from '@interstate/components/Snackbar';
import type { SplitButtonProps } from '@interstate/components/SplitButton';
import { SplitButton } from '@interstate/components/SplitButton';
import { StatusIndicator } from '@interstate/components/StatusIndicator';
import { Typography } from '@interstate/components/Typography';
import { ShareToCustomerDialogInterstate } from '../ShareToCustomerDialog/ShareToCustomerDialog.interstate';
import { ShareToSalesViewDialog } from '../ShareToSalesView/ShareToSalesViewDialog.interstate';
import { UpdateOfferDialog } from '../UpdateOfferDialog/UpdateOfferDialog';
import { useCustomerUI } from '../customerUI/useCustomerUI';
import { dealLockHooks, usePushToRouteOneCta } from '../hooks';
import { useDealCommentsPanel } from '@makemydeal/dr-shared-components';
import CheckoutAnywhereDrawer from '../dealActivity/activities/CheckoutAnywhereDrawer';
import PushToDms from '../dealActivity/activities/PushToDms.interstate';
import PushToRouteOneDialog from '../dealActivity/pushToRouteOne/PushToRouteOneDialog';
import PushToFIDialog from '../dealActivity/pushToFI/PushToFIDialog';
import PushToFIModal from '../dealActivity/pushToFI/PushToFIModal';
import { PrintPdfLoadingModal } from '../printPdfLoadingModal/PrintPdfLoadingModal';
import ReadyToSendDialogInterstate from '../readyToSendDialog/ReadyToSendDialog.interstate';
import { IncompleteTradeModal } from '../modals/components/IncompleteTradeModal.interstate';
import { DropdownsContainer, DealLockedActionsContainer, SnackbarWrapper } from './Footer.style.interstate';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

// libraries
import { dealerSelectors, featureToggleSelectors, selectedOfferSelectors, configActionCreators } from '@makemydeal/dr-shared-store';

import type { MenuProps, MenuReferenceType } from '@interstate/components/Menu';
import { Menu } from '@interstate/components/Menu';
import { BreakPoint } from '@makemydeal/dr-activities-common';

import * as appSelectors from '@makemydeal/dr-dash-store/selectors/appSelectors';
import * as baseDealAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/baseDealAnalyticsActionCreators';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as incompleteTradeModalSelectors from '@makemydeal/dr-dash-store/selectors/incompleteTradeModalSelectors';
import * as incompleteTradeModalActionCreators from '@makemydeal/dr-dash-store/actions/incompleteTradeModalActionCreators';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as creditAppSearchActionCreators from '@makemydeal/dr-dash-store/actions/creditAppSearchActionCreators';
import * as creditAppSelectors from '@makemydeal/dr-dash-store/selectors/creditAppSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as employeeDetailsSelectors from '@makemydeal/dr-dash-store/selectors/employeeDetailsSelectors';
import * as failedOfferUpdateActionCreators from '@makemydeal/dr-dash-store/actions/failedOfferUpdateActionCreators';
import * as failedOfferUpdateSelectors from '@makemydeal/dr-dash-store/selectors/failedOfferUpdateSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as printPdfActionCreator from '@makemydeal/dr-dash-store/actions/printPdfActionCreator';
import * as proposalActionCreators from '@makemydeal/dr-dash-store/actions/proposalActionCreators';
import * as pushToDarwinActionCreators from '@makemydeal/dr-dash-store/actions/pushToDarwinActionCreators';
import * as dealHistoryActionCreators from '@makemydeal/dr-dash-store/actions/dealHistoryActionCreator';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as unsavedChangesSelectors from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import * as incentivesSelectors from '@makemydeal/dr-dash-store/selectors/incentivesSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as dealValidationSelectors from '@makemydeal/dr-dash-store/selectors/dealValidationSelectors';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import useOpenInDarwin from '@makemydeal/dr-dash-store/hooks/useOpenInDarwin';

import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { CUDL_PROVIDER_CONFIG, ROUTE_ONE_PROVIDER_CONFIG } from '@makemydeal/dr-dash-types';
import { apiConfigConsts, offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { getQuoteMode } from '@makemydeal/dr-dash-store/selectors/quoteSelectors';
import PaymentRecalculatingDialog from '../PaymentRecalculatingDialog/PaymentRecalculatingDialog';
import { UpdateDraftDialog } from '../UpdateOfferDialog/UpdateDraftDialog';
import { FailedUpdateOffer } from '../common/FailedUpdateOffer.interstate';
import {
    Action,
    DAS_SUCCESS_MESSAGE,
    DEAL_REVIEW_AND_SUBMISSION_DIALOG_HEADER,
    DEAL_REVIEW_AND_SUBMISSION_DIALOG_PROMPT
} from '../constants';
import DealComments from '../dealComments/DealComments';
import { TemplateButtonsBarWithBuilderMode } from '../dealTemplates/dealTemplatesDrawer/TemplateButtonsBarWithBuilderMode';
import { FunctionsModal } from '../header/FunctionsModal.interstate';
import { PrintPreviewSlideOut } from '../printingProposals/PrintPreviewSlideOut';
import { ProposalsSideOut } from '../proposals/ProposalsSideOut';
import { UpdateOfferWithFSEventsDialog } from '../UpdateOfferDialog/UpdateOfferWithFSEventsDialog';

import {
    createOpeningProposalSettingsEvent,
    createPrintingSelectedProposalEvent,
    createFourSquarePrintLayoutSelectedEvent
} from '../dealTemplates/analytics/cdlEventCreators';
import UnsavedChangesModal from '../common/UnsavedChangesModal.interstate';
import SendToCustomerDialogInterstate from '../sendToCustomerDialog/SendToCustomerDialog.interstate';
import ContractFormTypeModal from '../generateContract/ContractFormTypeModal.interstate';
import { useCreditAppShareLink, useDocUploadShareLink } from '../hooks/useActivityShareLink';
import { useHandleGenerateContract } from '../hooks/useGenerateContract';
import { useIncompleteTradeGuard } from '../hooks/useIncompleteTradeGuard';
import { useValidationGuard } from '../hooks/useValidationGuard';
import { ReportIssueSlideOut } from '../reportIssue/ReportIssueSlideOut';
import { ReportIssueSlideOutV2 } from '../reportIssue/ReportIssueSlideOutV2';
import { blurActiveField } from '../utils/blurActiveField';

enum ActionTypes {
    UPDATE = 'UPDATE',
    DARWIN = 'DARWIN',
    BEGIN_CHECKOUT = 'BEGIN_CHECKOUT',
    MANAGE_DOCUMENTS = 'MANAGE_DOCUMENTS',
    OPEN_IN_FI = 'OPEN_IN_FI',
    OPEN_IN_DARWIN = 'OPEN_IN_DARWIN',
    DMS = 'DMS',
    DEAL_REVIEW = 'DEAL_REVIEW',
    GENERATE_CONTRACT = 'GENERATE_CONTRACT',
    DESKING = 'DESKING',
    ROUTE_ONE = 'ROUTE_ONE',
    CUDL = 'CUDL',
    FUNCTIONS = 'FUNCTIONS',
    CREDIT_APP_SEARCH = 'CREDIT_APP_SEARCH',
    OFFER_HISTORY = 'OFFER_HISTORY',
    AUDIT_LOG = 'AUDIT_LOG',
    REPORT_AN_ISSUE = 'REPORT_AN_ISSUE',
    MANAGE_TEMPLATES = 'MANAGE_TEMPLATES',
    SEND_TO_DEALER_TRACK = 'SEND_TO_DEALER_TRACK'
}

enum ShareActionTypes {
    SHARE_TO_SHOW_ROOM = 'ShareToShowroom',
    GET_DEAL_LINK = 'GetDealLink',
    SHARE_TO_CUSTOMER = 'ShareToCustomer',
    // Must stay out of EXIT_ACTION_TYPES below: `useActivityShareLink` already owns the validation guard, so
    // adding them here would guard the same click twice.
    SHARE_CREDIT_APP_LINK = 'ShareCreditAppLink',
    SHARE_DOC_UPLOAD_LINK = 'ShareDocUploadLink'
}

const actionCreatorMap = {
    [Action.CONTINUE_IN_DESKING]: deskingActionCreators.continueInDesking,
    [Action.SEND_TO_ECOMMERCE]: deskingActionCreators.sendToEcommerce
} as const;

const readyToSendDialogContentMap = {
    [Action.CONTINUE_IN_DESKING]: {
        dialogHeader: 'Ready to Continue in Desking',
        dialogPrompt: 'Clicking "Yes" will update the offer and open Connect Desking. Do you want to continue?',
        lienholderPrompt:
            'Clicking "Yes" will update the offer and open Connect Desking. Lienholder information on a cash deal will be lost. Do you want to continue?'
    },
    [Action.SEND_TO_ECOMMERCE]: {
        dialogHeader: 'Ready to Send to Ecommerce?',
        dialogPrompt: 'Clicking "Yes" will send you to Ecommerce. Are you sure you want to continue?'
    }
} as const;

const toggler = (prev: boolean) => !prev;

// MANAGE_DOCUMENTS and OPEN_IN_FI are intentionally excluded: they're gated only by dealRefId presence
const EXIT_ACTION_TYPES: Array<ActionTypes | ShareActionTypes> = [
    ActionTypes.BEGIN_CHECKOUT,
    ActionTypes.DEAL_REVIEW,
    ActionTypes.DARWIN,
    ActionTypes.OPEN_IN_DARWIN,
    ActionTypes.DMS,
    ActionTypes.DESKING,
    ActionTypes.ROUTE_ONE,
    ActionTypes.CUDL,
    ActionTypes.MANAGE_TEMPLATES,
    ShareActionTypes.SHARE_TO_CUSTOMER,
    ShareActionTypes.GET_DEAL_LINK,
    ShareActionTypes.SHARE_TO_SHOW_ROOM
];

const ActionsInterstate = ({
    buttonStyle,
    buttonSize,
    menuSize
}: {
    buttonStyle?: ButtonProps['buttonStyle'];
    buttonSize?: ButtonProps['size'];
    menuSize?: MenuProps['size'];
}) => {
    const dispatch = useDispatch();
    const store = useStore<StateTree>();

    const actionsEnabled = useSelector(appSelectors.isLazyInitCompleteForSmartInit);
    const isLatestPushToDmsPlusEnabled = useSelector(dealXgDetailsSelectors.getLatestPushToDmsPlus);
    const dealRefId = useSelector(offerSelectors.getDealRefId) || offerSessionUtils.getSessionValue(apiConfigConsts.FS_DEAL_REF_ID);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isDarwinEnabled = useSelector(dealerSelectors.isDarwinEnabled);
    const enablePushToDarwin =
        useSelector(featureToggleSelectors.isPushToDarwinEnabled) && isDarwinEnabled && currentOfferType !== BALLOON;
    const enablePushToDms = useSelector(dealerSelectors.getDMSIntegrationToggle).toLowerCase() !== 'off';
    const enableBeginCheckout = useSelector(dealerSelectors.enableCheckoutAnywhereToggle) && currentOfferType !== BALLOON;
    const enableDigitalDealJacket = useSelector(dealerSelectors.enableDigitalDealJacket);
    const isTraditionalMode = useSelector(dealerSelectors.getIsTraditionalMode);
    const isSalesIntakeMode = useSelector(dealerSelectors.isSalesIntakeMode);
    const dealerTrackDeepLinkURL = useSelector((state: StateTree) =>
        configSelectors.buildDealertrackUrlWithDealRefId(state, dealRefId)
    );
    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const isMVSalesTeamSlideoutEnabled = useSelector(featureToggleSelectors.isMVSalesTeamSlideoutEnabled);
    const isEnableMVCreditAppSearchAndMerge = useSelector(featureToggleSelectors.isEnableMVCreditAppSearchAndMerge);
    const isSendToDealerTrackEnabled = useSelector(featureToggleSelectors.isSendToDealerTrackEnabled);
    const hasCustomerForDealerTrack = Boolean(useSelector(offerSelectors.getOfferCommonConsumerId));
    const isReportIssueEnabled = useSelector(featureToggleSelectors.isReportIssueEnabled);
    const isReportIssueV2Enabled = useSelector(featureToggleSelectors.isReportIssueV2Enabled);
    const isCommitActiveFieldOnSaveEnabled = useSelector(featureToggleSelectors.isCommitActiveFieldOnSaveEnabled);

    const isGenerateContractsEnabled = useSelector(dealerSelectors.getEnableGenerateContracts);
    const isGenerateContractingUiOptionEnabled = useSelector(contractingSelectors.isGenerateContractingUiOptionEnabled);
    const isContinueToDeskingDisabled = useSelector(dealerSelectors.isContinueToDeskingDisabled);
    const copyToDeskingURL = useSelector(configSelectors.getCopyToDeskingURL);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const enablePushToCUDL = useSelector(featureToggleSelectors.enablePushToCUDL);
    const enableCudlIntegration = useSelector(dealerSelectors.getIsCudlIntegrationEnabled);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const isBusinessShopper = useSelector(offerSelectors.getIsBusinessShopper);
    const enablePushToCUDLCTA =
        enablePushToCUDL &&
        enableCudlIntegration &&
        (offerType === FINANCE || offerType === LEASE) &&
        !isFlexBuyPayment &&
        !isBusinessShopper;
    const creditAppCompleted = useSelector(creditAppSelectors.isCreditAppCompleted);
    const routeOneCta = usePushToRouteOneCta();
    const isGenericPushToR1JPath = routeOneCta.isGenericPath;

    const enableDealComments = useSelector(dealerSelectors.isDealCommentsMVEnabled);
    const dealCommentsReferenceId = useSelector(offerReduxSelectors.getDealXgId);
    const dealCommentsCommonOrgId = useSelector(offerReduxSelectors.getCommonOrgId);
    const draftIsBeingSaved = useSelector(offerInfoSelectors.getIsDraftBeingSaved);
    const draftHasBeenSaved = useSelector(offerInfoSelectors.getHasDraftBeenSaved);
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);
    const employeesHaveUpdatedSuccessFully = useSelector(employeeDetailsSelectors.getSendUpdateSuccess);
    const employeesAreBeingUpdated = useSelector(employeeDetailsSelectors.getSendingUpdate);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isDealXgRecordMissed = useSelector(offerReduxSelectors.getIsDealXgRecordMissed);
    const pushToDmsRef = useRef<{ handleConfirmPushToDmsShow: () => void }>();

    const showUpdateOfferForFSEvents = useSelector(baseDealSelectors.getShowUpdateOfferForFSEvents);
    const [showSaveDraft, setShowSaveDraft] = useState(false);
    const [showDraftSuccessSnackbar, setSaveDraftSuccessNotification] = useState(false);

    const [showDraftFailureSnackbar, setSaveDraftFailureNotification] = useState(false);
    const [showEmployeeUpdateNotification, setShowEmployeeUpdateNotification] = useState(null as boolean | null);
    const [showPushToDropdown, setPushToDropdown] = useState<null | HTMLElement>(null);
    const [actionsDropdown, setActionsDropdown] = useState<null | HTMLElement>(null);
    const [showUpdateOffer, setShowUpdateOffer] = useState(false);
    const [showReadyToSendDialogSave, setShowReadyToSendDialogSave] = useState(false);
    const openPushToDropdown = Boolean(showPushToDropdown);
    const [openActionsDropdown, setOpenActionsDropdown] = useState(false);
    const [showUpdateOfferForBeginCheckout, setShowUpdateOfferForBeginCheckout] = useState(false);
    const [isCheckoutAnywhereDrawerEnabled, setIsCheckoutAnywhereDrawerEnabled] = useState(false);
    const [showReportIssueSlideout, setShowReportIssueSlideout] = useState(false);
    const [successNotification, setSuccessNotification] = useState(false);
    const [isPaymentCalculationComplete, setIsPaymentCalculationComplete] = useState(false);
    const [showDasSuccessSnackbar, setShowDasSuccessSnackbar] = useState(false);
    const [isDasApplyInProgress, setIsDasApplyInProgress] = useState(false);
    const [dasApplySuccessful, setDasApplySuccessful] = useState(false);

    const [openFni, setOpenFni] = useState(false);
    const [openDdj, setOpenDdj] = useState(false);
    const [currentAction, setCurrentAction] = useState<Action>(Action.CONTINUE_IN_DESKING);
    const [showShareDropdown, setShareDropdown] = useState<null | HTMLElement>(null);
    const openShareDropdown = Boolean(showShareDropdown);
    const crmIntegrationToggle = useSelector(dealerSelectors.getCrmIntegrationToggle);
    const { showCustomerCard } = useCustomerUI();
    const largerThanSM = useMediaQuery({ query: `(min-width: ${BreakPoint.SMALL})` });
    const [showShareToSalesViewDialog, setShowShareToSalesViewDialog] = useState(false);
    const [showShareToCustomerDialog, setShowShareToCustomerDialog] = useState(false);
    const [showGetDealLinkDialog, setShowGetDealLinkDialog] = useState(false);
    const [showPushToR1Dialog, setShowPushToR1Dialog] = useState(false);
    const [openPushToFiProviderId, setOpenPushToFiProviderId] = useState<string | null>(null);
    const [showFunctionsModal, setShowFunctionsModal] = useState(false);
    const [showPaymentRecalculatingDialog, setShowPaymentRecalculatingDialog] = useState(false);
    const [shouldShowSaveDraftBtn, setShouldShowSaveDraftBtn] = useState(false);
    const isMobileView = useMediaQuery({ query: `(max-width: ${BreakPoint.PHONE})` });
    const showSaveDraftButton = useSelector(unsavedChangesSelectors.currentOfferHasUnsavedChanges);
    const [showReadyToSendDialog, setShowReadyToSendDialog] = useState(false);
    const initDealExchangeDealVersion = useSelector(selectedOfferSelectors.getSelectedOfferDealXgVersion);
    const latestDealExchangeVersion = useSelector(offerInfoSelectors.getDealXgVersion);
    const redirectionLink = useSelector(dealXgDetailsSelectors.getRedirectionLink);
    const dealExchangeDealId = useSelector(selectedOfferSelectors.getSelectedOfferDealXgId);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerName = useSelector(dealerSelectors.getDealerName);
    const dealComments = useDealCommentsPanel({
        isEnabled: enableDealComments,
        referenceId: dealCommentsReferenceId,
        commonOrgId: dealCommentsCommonOrgId
    });
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    const outsideLienholderData = useSelector(offerReduxSelectors.getOutsideLienholder);
    const hasLienholderData = !!outsideLienholderData?.lienHolder;
    const [readyToSendDialogPrompt, setReadyToSendDialogPrompt] = useState<string>('');
    const isDealTemplatesViewEnabled = useSelector(configSelectors.isDealTemplatesViewEnabled);
    const isDealAuditLog = useSelector(dealerSelectors.isDealAuditLog);
    const isEmployee = useSelector(dealValidationSelectors.getIsEmployee);

    const isPaymentCallPending = useSelector(baseDealSelectors.isPaymentCallPending);
    const isCalculatingPayment = useSelector(paymentSelectors.isCalculatingPayment);
    const showFailedUpdateOfferModal = useSelector(failedOfferUpdateSelectors.getShowFailedUpdateOfferModal);
    const [disableGenerateContractsOfferTypeChange, setDisableGenerateContractsOfferTypeChange] = useState(false);
    // DAS selectors
    const isDasApplied = useSelector(dueAtSigningSelectors.getIsApplied);
    const isDasCalculating = useSelector(dueAtSigningSelectors.getIsCalculating);
    const showDasSlideOut = useSelector(dueAtSigningSelectors.getIsSlideOutOpen);
    const isQuoteMode = useSelector(getQuoteMode);

    const appliedTemplateId = useSelector(dealTemplateSelectors.getAppliedDealTemplateId);

    const incentiveValidationErrorMessage = useSelector(incentivesSelectors.getIncentiveValidationErrorMessage);
    const hasIncentiveValidationErrors = useSelector(incentivesSelectors.getHasIncentiveValidationErrors);
    const skipCtaValidation = useSelector(compositeSelectors.getIsTraditionalOrIntakeWithoutVehicle);
    const saveGate = useValidationGuard('save', {
        skipValidationKeys: isDealXgRecordMissed ? ['buyer', 'vehicle'] : ['vehicle'],
        skipGuardValidation: skipCtaValidation
    });

    const dashboardSaveGate = useValidationGuard('save', {
        skipValidationKeys: isDealXgRecordMissed ? ['buyer', 'vehicle'] : ['vehicle'],
        skipGuardValidation: skipCtaValidation && !isQuoteMode
    });
    const exitGate = useValidationGuard('exit');
    // Sales View/Showroom copes with a deal that has no selling vehicle, unlike the other exit targets.
    const vehicleOptionalExitGate = useValidationGuard('exit', { skipValidationKeys: ['vehicle'] });
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

    const isConsolidatedShareMenuEnabled = useSelector(featureToggleSelectors.isConsolidatedShareMenuEnabled);
    const creditAppShareLink = useCreditAppShareLink();
    const docUploadShareLink = useDocUploadShareLink();
    // Depend on the actions alone - the whole hook result also carries `dialogProps.show`, which would rebuild
    // the menu every time a dialog opens or closes.
    const shareCreditAppLink = creditAppShareLink.shareLink;
    const shareDocUploadLink = docUploadShareLink.shareLink;

    const openInDarwin = useOpenInDarwin();

    const [printPreviewShown, setPrintPreviewShown] = useState(false);

    const showPaymentRecalculationCompletionSnackbar = useRef(false);
    // Tracks that the recalculating dialog was already shown for the current pending state, so a
    // retry can bypass a stranded (non-calculating) flag instead of blocking the user forever.
    const hasPromptedRecalcRef = useRef(false);
    const initialOfferType = useRef<OfferType>(currentOfferType);
    const readyToSendDialogHeader =
        currentAction === Action.SEND_TO_ECOMMERCE
            ? DEAL_REVIEW_AND_SUBMISSION_DIALOG_HEADER
            : readyToSendDialogContentMap[currentAction].dialogHeader;

    const isBaseDealer = useSelector(dealerSelectors.isBaseDealExperience);
    const showIncompleteTradeModal = useSelector(incompleteTradeModalSelectors.getShowIncompleteTradeModal);

    const updateOfferDialogOnHideCallback = () => setShowUpdateOffer(false);
    const { handleGenerateContract, showContractFormTypeModal, handleConfirmContractFormType, handleCloseContractFormTypeModal } =
        useHandleGenerateContract();
    const tradeGuard = useIncompleteTradeGuard();
    const handleGenerateContractWithCheck = useMemo(
        () => tradeGuard.wrap(handleGenerateContract),
        [tradeGuard.wrap, handleGenerateContract]
    );

    /*istanbul ignore next*/
    const handleFailedUpdateOfferClose = useCallback(() => {
        dispatch(failedOfferUpdateActionCreators.hideFailedUpdateOfferModal());
        updateOfferDialogOnHideCallback();
    }, [dispatch, updateOfferDialogOnHideCallback]);

    // callback not executed by this component, ignore coverage
    // istanbul ignore next
    const toggleDraftSuccessSnackbar = () => setSaveDraftSuccessNotification(toggler);
    // callback not executed by this component, ignore coverage
    // istanbul ignore next
    const toggleDraftFailureSnackbar = () => setSaveDraftFailureNotification(toggler);
    const closeReadyToSendDialog = useCallback(() => {
        setShowReadyToSendDialog(false);
    }, [setShowReadyToSendDialog]);

    // Hide UpdateOffer dialog when incomplete trade modal shows
    useEffect(() => {
        if (showIncompleteTradeModal && showUpdateOffer) {
            setShowUpdateOffer(false);
        }
    }, [showIncompleteTradeModal, showUpdateOffer]);

    useEffect(() => {
        setTimeout(() => {
            setShouldShowSaveDraftBtn(showSaveDraftButton);
        }, 0);
    }, [showSaveDraftButton]);

    // istanbul ignore next
    useEffect(() => {
        if (dealRefId && isEcommerceDeal && creditAppCompleted) {
            if (initialOfferType.current !== currentOfferType && initialOfferType.current !== null) {
                setDisableGenerateContractsOfferTypeChange(true);
            }
            if (disableGenerateContractsOfferTypeChange && initialOfferType.current === currentOfferType) {
                setDisableGenerateContractsOfferTypeChange(false);
            }
        }
    }, [currentOfferType]);

    // istanbul ignore next
    useEffect(() => {
        if (dealRefId && isEcommerceDeal) {
            if (disableGenerateContractsOfferTypeChange) {
                setDisableGenerateContractsOfferTypeChange(false);
            }
            initialOfferType.current === currentOfferType;
        }
    }, [dealRefId]);

    /* istanbul ignore next */
    useEffect(() => {
        if (!isPaymentCallPending) {
            // Flag cleared (recalculation completed or reset) — arm the dialog for the next pending state.
            hasPromptedRecalcRef.current = false;
        }
        if (!isPaymentCallPending && showPaymentRecalculationCompletionSnackbar.current && !showPaymentRecalculatingDialog) {
            setIsPaymentCalculationComplete(true);
        }
    }, [isPaymentCallPending, showPaymentRecalculatingDialog]);

    useEffect(() => {
        if (showSaveDraft && !draftIsBeingSaved) {
            setShowSaveDraft(false);
            draftHasBeenSaved ? toggleDraftSuccessSnackbar() : toggleDraftFailureSnackbar();
        }
    }, [draftIsBeingSaved, draftHasBeenSaved, showSaveDraft]);

    useEffect(() => {
        if (isDasCalculating && !isDasApplyInProgress) {
            setIsDasApplyInProgress(true);
        }

        if (!isDasCalculating) {
            if (isDasApplyInProgress) {
                setIsDasApplyInProgress(false);
            } else {
                if (isDasApplied) {
                    setDasApplySuccessful(true);
                } else if (dasApplySuccessful) {
                    setDasApplySuccessful(false);
                }
            }
        }
    }, [isDasApplied, isDasCalculating, isDasApplyInProgress]);

    useEffect(() => {
        /* istanbul ignore next */
        if (dasApplySuccessful && !showDasSlideOut) {
            setShowDasSuccessSnackbar(true);
            setDasApplySuccessful(false); // Reset for next time
        }
    }, [dasApplySuccessful, showDasSlideOut]);

    /* istanbul ignore next */
    const onCloseEmployeeUpdateNotification = () => setShowEmployeeUpdateNotification(false);

    useEffect(() => {
        if (showEmployeeUpdateNotification === null) {
            setShowEmployeeUpdateNotification(false);
            return;
        }

        /* istanbul ignore next */
        if (!employeesAreBeingUpdated) {
            setShowEmployeeUpdateNotification(true);
        }
    }, [employeesAreBeingUpdated, employeesHaveUpdatedSuccessFully]);

    const dispatchAction = useCallback(
        (action: Action) => {
            const actionCreator = actionCreatorMap[action];
            if (actionCreator) {
                dispatch(actionCreator());
            }
        },
        [dispatch]
    );

    const handleActionClick = useCallback(
        (action: Action) => {
            setCurrentAction(action);
            let prompt = '';
            if (action === Action.SEND_TO_ECOMMERCE) {
                prompt = DEAL_REVIEW_AND_SUBMISSION_DIALOG_PROMPT;
            } else if (currentOfferType === CASH && hasLienholderData) {
                prompt = readyToSendDialogContentMap[action].lienholderPrompt;
            } else {
                prompt = readyToSendDialogContentMap[action].dialogPrompt;
            }
            setReadyToSendDialogPrompt(prompt);
            setShowReadyToSendDialog(true);
            dispatchAction(action);
        },
        [setCurrentAction, setShowReadyToSendDialog, dispatchAction, hasLienholderData]
    );

    const handleContinueInDesking = useCallback(() => handleActionClick(Action.CONTINUE_IN_DESKING), [handleActionClick]);
    const handleSendToEcommerce = useCallback(() => handleActionClick(Action.SEND_TO_ECOMMERCE), [handleActionClick]);

    const handleShareToCustomer = () => {
        tradeGuard.wrap(
            () => {
                dispatch(offerActionCreators.saveOffer());
                if (crmIntegrationToggle) {
                    showCustomerCard(true, { tab: 'inbox', messageContentSelector: urlSelector, waitForOfferSave: true });
                } else {
                    setShowShareToCustomerDialog(true);
                }
            },
            // UI-only path when guard intercepts: middleware handles saveOffer() via PAYMENT_RECEIVED
            () => {
                // istanbul ignore next
                if (crmIntegrationToggle) {
                    showCustomerCard(true, { tab: 'inbox', messageContentSelector: urlSelector, waitForOfferSave: true });
                } else {
                    setShowShareToCustomerDialog(true);
                }
            }
        )();
    };

    const handleShareToSalesView = () => {
        if (isTraditionalMode) {
            dispatch(printPdfActionCreator.printPdfResetSuccess());
            setPrintPreviewShown(true);
        } else {
            setShowShareToSalesViewDialog(true);
        }
    };
    const handlePaymentRecaclulatingDialogShow = () => {
        logNewRelicPageAction('MV:Save - Payment Recalculating Dialog Shown', {
            dealerId,
            dealXgId: dealExchangeDealId ?? '',
            offerType: currentOfferType,
            isCalculatingPayment,
            isRetry: hasPromptedRecalcRef.current
        });
        setShowPaymentRecalculatingDialog(true);
    };

    const closePaymentRecaclulatingDialog = () => {
        setShowPaymentRecalculatingDialog(false);
    };

    const handleGetDealLink = () => {
        tradeGuard.wrap(
            () => {
                dispatch(offerActionCreators.saveOffer());
                setShowGetDealLinkDialog(true);
            },
            // UI-only path when guard intercepts: middleware handles saveOffer() via PAYMENT_RECEIVED
            () => setShowGetDealLinkDialog(true)
        )();
    };

    const handleContinueToDeskingSaved = useCallback(() => {
        closeReadyToSendDialog();
        window.open(copyToDeskingURL + dealExchangeDealId, '_blank');
    }, [closeReadyToSendDialog, copyToDeskingURL, dealExchangeDealId]);

    const handleSendToEcommerceSaved = useCallback(() => {
        closeReadyToSendDialog();
        const dealExchangeVersion = latestDealExchangeVersion || initDealExchangeDealVersion;
        window.location.assign(`${redirectionLink}/${dealExchangeDealId}/version/${dealExchangeVersion}`);
    }, [closeReadyToSendDialog, latestDealExchangeVersion, initDealExchangeDealVersion, redirectionLink, dealExchangeDealId]);

    // istanbul ignore next # this is invoked WAY down the pipeline and out of scope here.
    const urlSelector = (state: any) => {
        const url = offerInfoSelectors.getShopperURL(state);
        return `\n${url}`;
    };

    const handleReadyToSendSaved = useCallback(() => {
        switch (currentAction) {
            case Action.CONTINUE_IN_DESKING:
                return handleContinueToDeskingSaved();
            case Action.SEND_TO_ECOMMERCE:
                return handleSendToEcommerceSaved();
        }
    }, [currentAction, handleContinueToDeskingSaved, handleSendToEcommerceSaved]);

    const handleSaveOfferForBeginCheckout = () => {
        tradeGuard.wrap(
            () => {
                dispatch(deskingActionCreators.beginCheckoutClicked());
                dispatch(offerActionCreators.saveOffer());
                setShowUpdateOfferForBeginCheckout(true);
            },
            // UI-only path when guard intercepts: middleware handles saveOffer() via PAYMENT_RECEIVED
            () => {
                dispatch(deskingActionCreators.beginCheckoutClicked());
                setShowUpdateOfferForBeginCheckout(true);
            }
        )();
    };

    useEffect(() => {
        //istanbul ignore next

        if (!offerIsBeingSaved && offerHasBeenSaved && !showIncompleteTradeModal) {
            if (showUpdateOfferForFSEvents && dealRefId) {
                // dealRefId gets appended async, not right away after a save op.
                // Adding dealRefId as a dependency ensures this effect re-runs when dealRefId becomes available
                setSuccessNotification(true);
                dispatch(baseDealActionCreators.setShowUpdateOfferForFSEvents(false));
                if (openFni) {
                    window.open(dealerTrackDeepLinkURL, '_blank');
                    setOpenFni(false);
                }
                if (openDdj) {
                    dispatch(navigationActionCreators.navigateToDDJ());
                    setOpenDdj(false);
                }
                return;
            }
            if (showUpdateOffer) {
                setSuccessNotification(true);
                setShowUpdateOffer(false);
            }
        }
    }, [offerIsBeingSaved, offerHasBeenSaved, showUpdateOffer, showUpdateOfferForFSEvents, showIncompleteTradeModal, dealRefId]);

    // hotkey for save
    useEffect(() => {
        hotkeys('alt+s,CapsLock+alt+s', { keyup: true, keydown: false }, function (event) {
            event.preventDefault();
            if (actionsEnabled) handleSaveClick(true);
        });

        return () => {
            hotkeys.unbind('alt+s,CapsLock+alt+s');
        };
    }, [actionsEnabled]);

    const handlePushToButtonClose = () => setPushToDropdown(null);

    const handleActionsButtonClose = () => setOpenActionsDropdown(false);

    //istanbul ignore next
    const snackbarOnCloseCallback = () => setSuccessNotification(false);

    const dasSuccessSnackbarOnCloseCallback = () => setShowDasSuccessSnackbar(false);

    /* istanbul ignore next */
    const closePaymentNotificationSnackbar = () => {
        setIsPaymentCalculationComplete(false);
        showPaymentRecalculationCompletionSnackbar.current = false;
    };
    const commitActiveField = useCallback(() => {
        if (isCommitActiveFieldOnSaveEnabled) {
            blurActiveField();
        }
    }, [isCommitActiveFieldOnSaveEnabled]);

    const handlePushToDarwin = useMemo(
        () =>
            tradeGuard.wrap(() => {
                commitActiveField();
                dispatch(interactionAnalyticsActionCreators.pushToDarwinButton());
                // Clear bypass flag so PAYMENT_RECEIVED doesn't trigger an extra save —
                // Darwin middleware handles the save via saveOfferRSAA internally.
                dispatch(incompleteTradeModalActionCreators.clearBypassIncompleteTradeCheck());
                dispatch(pushToDarwinActionCreators.pushToDarwin());
            }),
        [tradeGuard.wrap, dispatch, commitActiveField]
    );

    const handleOpenInDarwin = useMemo(
        () =>
            tradeGuard.wrap(() => {
                commitActiveField();
                // Clear bypass flag so PAYMENT_RECEIVED doesn't trigger an extra save —
                // Darwin middleware handles the save via saveOfferRSAA internally.
                dispatch(incompleteTradeModalActionCreators.clearBypassIncompleteTradeCheck());
                openInDarwin();
            }),
        [tradeGuard.wrap, dispatch, openInDarwin, commitActiveField]
    );

    const handlePushToDms = useMemo(
        () =>
            tradeGuard.wrap(() => {
                // Clear bypass flag so PAYMENT_RECEIVED doesn't trigger an extra save —
                // PushToDms component handles the save internally.
                dispatch(incompleteTradeModalActionCreators.clearBypassIncompleteTradeCheck());
                // istanbul ignore next - ref to PushToDms component, tested in FinishDealCard PushToDms unit test
                pushToDmsRef.current?.handleConfirmPushToDmsShow();
            }),
        [tradeGuard.wrap, dispatch]
    );

    const handleSaveOffer = () => {
        tradeGuard.wrap(
            () => {
                dispatch(offerActionCreators.saveOffer());
                setShowUpdateOffer(true);
            },
            // UI-only path when guard intercepts: middleware handles saveOffer() via PAYMENT_RECEIVED
            () => setShowUpdateOffer(true)
        )();
    };

    const handleSendToDealerTrack = () => {
        saveGate.wrap(
            tradeGuard.wrap(() => {
                dispatch(offerActionCreators.sendToDealerTrackClicked());
                setShowUpdateOffer(true);
            })
        )();
    };

    const openUniFiDeepLinkUrl = () => {
        if (showSaveDraftButton || !dealRefId) {
            tradeGuard.wrap(
                () => {
                    dispatch(interactionAnalyticsActionCreators.openDealFIBtn());
                    setOpenFni(true);
                    dispatch(baseDealActionCreators.setShowUpdateOfferForFSEvents(true));
                    dispatch(offerActionCreators.saveOffer());
                },
                // UI-only path when guard intercepts: middleware handles saveOffer() via PAYMENT_RECEIVED
                () => {
                    dispatch(interactionAnalyticsActionCreators.openDealFIBtn());
                    setOpenFni(true);
                    dispatch(baseDealActionCreators.setShowUpdateOfferForFSEvents(true));
                }
            )();
        } else {
            dispatch(interactionAnalyticsActionCreators.openDealFIBtn());
            window.open(dealerTrackDeepLinkURL, '_blank');
        }
    };

    const handleManageDocumentsClick = () => {
        logNewRelicPageAction('MV:DDJ - Manage Documents Click', {
            dealerId,
            dealXgId: dealExchangeDealId ?? ''
        });
        if (!dealRefId) {
            tradeGuard.wrap(
                () => {
                    dispatch(interactionAnalyticsActionCreators.manageDocumentsButton());
                    setOpenDdj(true);
                    dispatch(baseDealActionCreators.setShowUpdateOfferForFSEvents(true));
                    dispatch(offerActionCreators.saveOffer());
                },
                // UI-only path when guard intercepts: middleware handles saveOffer() via PAYMENT_RECEIVED
                () => {
                    dispatch(interactionAnalyticsActionCreators.manageDocumentsButton());
                    setOpenDdj(true);
                    dispatch(baseDealActionCreators.setShowUpdateOfferForFSEvents(true));
                }
            )();
        } else {
            dispatch(interactionAnalyticsActionCreators.manageDocumentsButton());
            dispatch(navigationActionCreators.navigateToDDJ());
        }
    };

    const handlePushToR1DialogShow = useMemo(
        () => tradeGuard.wrap(() => setShowPushToR1Dialog(true)),
        [tradeGuard.wrap, setShowPushToR1Dialog]
    );

    const handleHidePushToR1Dialog = () => {
        setShowPushToR1Dialog(false);
    };

    const handleShowPushToFiDialog = useCallback(
        (providerId: string) => tradeGuard.wrap(() => setOpenPushToFiProviderId(providerId))(),
        [tradeGuard.wrap]
    );

    const handleHidePushToFiDialog = useCallback(() => setOpenPushToFiProviderId(null), []);

    const handleManageTemplates = () => {
        dispatch(configActionCreators.turnOnTemplateView());
    };

    const onHideUpdateOfferForFSEvents = () => {
        setOpenDdj(false);
        setOpenFni(false);
        dispatch(baseDealActionCreators.setShowUpdateOfferForFSEvents(false));
    };

    const handleMenuActionsFn = (actionType: ActionTypes | ShareActionTypes) => {
        switch (actionType) {
            case ActionTypes.DARWIN:
                handlePushToDarwin();
                handlePushToButtonClose();
                break;
            case ActionTypes.DMS:
                handlePushToDms();
                handlePushToButtonClose();
                break;
            case ActionTypes.BEGIN_CHECKOUT:
                handleSaveOfferForBeginCheckout();
                handleActionsButtonClose();
                break;
            case ActionTypes.MANAGE_DOCUMENTS:
                handleManageDocumentsClick();
                handleActionsButtonClose();
                break;
            case ActionTypes.OPEN_IN_FI:
                openUniFiDeepLinkUrl();
                handleActionsButtonClose();
                break;
            case ActionTypes.FUNCTIONS:
                setShowFunctionsModal(true);
                handleActionsButtonClose();
                break;
            case ActionTypes.DEAL_REVIEW:
                handleSendToEcommerce();
                handleActionsButtonClose();
                break;
            case ActionTypes.DESKING:
                handleContinueInDesking();
                handlePushToButtonClose();
                break;
            case ShareActionTypes.SHARE_TO_CUSTOMER:
                handleShareToCustomer();
                handleShareButtonClose();
                break;
            case ShareActionTypes.GET_DEAL_LINK:
                handleGetDealLink();
                handleShareButtonClose();
                break;
            case ShareActionTypes.SHARE_TO_SHOW_ROOM:
                handleShareToSalesView();
                handleShareButtonClose();
                break;
            case ActionTypes.ROUTE_ONE:
                isGenericPushToR1JPath
                    ? handleShowPushToFiDialog(ROUTE_ONE_PROVIDER_CONFIG.targetPlatformId)
                    : handlePushToR1DialogShow();
                handlePushToButtonClose();
                break;
            case ActionTypes.CUDL:
                handleShowPushToFiDialog(CUDL_PROVIDER_CONFIG.targetPlatformId);
                handlePushToButtonClose();
                break;
            case ActionTypes.CREDIT_APP_SEARCH:
                dispatch(creditAppSearchActionCreators.openCreditAppSearchSlideout());
                handleActionsButtonClose();
                break;
            case ActionTypes.OPEN_IN_DARWIN:
                handleOpenInDarwin();
                handleActionsButtonClose();
                break;
            case ActionTypes.OFFER_HISTORY:
                handleOpenOfferHistory();
                handleActionsButtonClose();
                break;
            case ActionTypes.AUDIT_LOG:
                navigateToAudit();
                handleActionsButtonClose();
                break;
            case ActionTypes.REPORT_AN_ISSUE:
                setShowReportIssueSlideout(true);
                handleActionsButtonClose();
                break;
            case ActionTypes.MANAGE_TEMPLATES:
                handleManageTemplates();
                break;
            case ActionTypes.SEND_TO_DEALER_TRACK:
                handleSendToDealerTrack();
                handleActionsButtonClose();
                break;
        }
    };

    const handleMenuActions = useCallback(
        (actionType: ActionTypes | ShareActionTypes) => {
            const gate = actionType === ShareActionTypes.SHARE_TO_SHOW_ROOM ? vehicleOptionalExitGate : exitGate;
            EXIT_ACTION_TYPES.includes(actionType) ? gate.wrap(handleMenuActionsFn)(actionType) : handleMenuActionsFn(actionType);
        },
        [exitGate, vehicleOptionalExitGate, handleMenuActionsFn]
    );

    const confirmAndSaveRequest = useCallback(
        (isSourceHotKeys?: boolean) => {
            if (isSourceHotKeys) {
                dispatch(baseDealAnalyticsActionCreators.clickedUpdateHotKey());
            } else {
                dispatch(baseDealAnalyticsActionCreators.clickedUpdate());
            }

            if (isLatestPushToDmsPlusEnabled) {
                setShowReadyToSendDialogSave(true);
            } else {
                handleSaveOffer();
            }
        },
        [dispatch, isLatestPushToDmsPlusEnabled, setShowReadyToSendDialogSave, handleSaveOffer]
    );
    // Note: Cancel order logic is now handled globally in offerMiddleware.ts
    // The middleware will automatically check if cancellation is needed before any save operation
    const rawSaveClick = useCallback(
        (isSourceHotKeys?: boolean) => {
            // Check for incentive validation errors first
            if (hasIncentiveValidationErrors) {
                setShowUnsavedChangesModal(true);
                return;
            }

            const state = store.getState();
            const paymentCallPending = baseDealSelectors.isPaymentCallPending(state);
            const calculatingPayment = paymentSelectors.isCalculatingPayment(state);

            if (!paymentCallPending) {
                confirmAndSaveRequest(isSourceHotKeys);
                return;
            }

            // Safety net: paymentCallPending can be stranded true by an edit that set the flag but
            // never produced a completing payment call. On a retry (the dialog was already shown for
            // this pending state), if no calculation is actually in flight the flag is stale — clear
            // it and allow the save instead of blocking the user forever. A genuinely in-flight
            // calculation keeps isCalculatingPayment true, so it still blocks correctly.
            if (hasPromptedRecalcRef.current && !calculatingPayment) {
                logNewRelicPageAction('MV:Save - Payment Recalculating Dialog Bypassed', {
                    dealerId,
                    dealXgId: dealExchangeDealId ?? '',
                    offerType: currentOfferType
                });
                hasPromptedRecalcRef.current = false;
                dispatch(offerActionCreators.updatePaymentCallPendingState(false));
                confirmAndSaveRequest(isSourceHotKeys);
                return;
            }

            // First attempt (or a calculation genuinely running): prompt and wait for the retry.
            // Show snackbar once the payment call is complete
            showPaymentRecalculationCompletionSnackbar.current = true;
            handlePaymentRecaclulatingDialogShow();
            hasPromptedRecalcRef.current = true;
        },
        [
            store,
            dispatch,
            confirmAndSaveRequest,
            handlePaymentRecaclulatingDialogShow,
            hasIncentiveValidationErrors,
            dealerId,
            dealExchangeDealId,
            currentOfferType
        ]
    );
    const handleSaveClick = useMemo(() => {
        const gatedSave = dashboardSaveGate.wrap(rawSaveClick);
        return (isSourceHotKeys?: boolean) => {
            commitActiveField();
            gatedSave(isSourceHotKeys);
        };
    }, [dashboardSaveGate.wrap, rawSaveClick, commitActiveField]);

    const handleOpenDealMentions = () => {
        dealComments.open();
        if (typeof newrelic === 'object') {
            newrelic.addPageAction('MV:DealComments - Panel Opened', {
                dealXgId: dealExchangeDealId ?? '',
                source: 'baseFooter'
            });
        }
    };

    const handleCloseDealMentions = () => {
        dealComments.close();
        if (typeof newrelic === 'object') {
            newrelic.addPageAction('MV:DealComments - Panel Closed', {
                dealXgId: dealExchangeDealId ?? '',
                source: 'baseFooter'
            });
        }
    };

    const handleOpenOfferHistory = () => {
        dispatch(dealHistoryActionCreators.openDealHistoryDrawer());
        logNewRelicPageAction('MV:DealHistory:Opened', {
            dealXgId: dealExchangeDealId ?? '',
            source: 'actionsDropdown',
            dealerId,
            dealerName
        });
    };

    // istanbul ignore next
    const handleSave = () => setShowReadyToSendDialogSave(false);

    const setShowReadyToSendDialogSaveOnNoOrHide = useCallback(
        () => setShowReadyToSendDialogSave(false),
        [setShowReadyToSendDialogSave]
    );

    const handleShareButtonClose = useCallback(() => setShareDropdown(null), [setShareDropdown]);

    const getToDealLinkDialogOnHide = useCallback(() => setShowGetDealLinkDialog(false), [setShowGetDealLinkDialog]);
    const shareToCustomerDialogOnHide = useCallback(() => setShowShareToCustomerDialog(false), [setShowShareToCustomerDialog]);

    const shareToSalesViewOnHideCallback = useCallback(() => setShowShareToSalesViewDialog(false), [setShowShareToSalesViewDialog]);
    const shareToSalesViewOnOfferSaveHide = useCallback(() => {
        setSuccessNotification(true);
        setShowShareToSalesViewDialog(false);
    }, [setSuccessNotification, setShowShareToSalesViewDialog]);
    const showDealReview = isEcommerceDeal && !isGenerateContractsEnabled && !isMvLiteEnabled;
    const showRouteOne = routeOneCta.showCta;
    const showCUDL = enablePushToCUDLCTA && !isMvLiteEnabled;
    const showDeskingOption = !isContinueToDeskingDisabled && isDealCentralExperience && !isMvLiteEnabled;

    const createDeskingOption = () => {
        if (showDeskingOption) {
            return [
                {
                    label: 'Desking',
                    value: ActionTypes.DESKING,
                    id: 'desking-option',
                    onSelect: () => {
                        handleMenuActions(ActionTypes.DESKING);
                    }
                }
            ];
        }
        return [];
    };

    const createRouteOneOption = () => {
        if (showRouteOne) {
            return [
                {
                    label: 'Route One',
                    value: ActionTypes.ROUTE_ONE,
                    id: 'route-one-option',
                    onSelect: () => {
                        handleMenuActions(ActionTypes.ROUTE_ONE);
                    }
                }
            ];
        }
        return [];
    };

    const createCUDLOption = () => {
        if (showCUDL) {
            return [
                {
                    label: 'CUDL',
                    value: ActionTypes.CUDL,
                    id: 'cudl-option',
                    onSelect: () => {
                        handleMenuActions(ActionTypes.CUDL);
                    }
                }
            ];
        }
        return [];
    };

    const pushToOptions = useMemo(
        () =>
            [
                {
                    label: 'DMS',
                    value: ActionTypes.DMS,
                    id: 'dms-option',
                    disabled: !enablePushToDms,
                    onSelect: () => {
                        handleMenuActions(ActionTypes.DMS);
                    }
                },
                !isMvLiteEnabled && {
                    label: 'Darwin',
                    value: ActionTypes.DARWIN,
                    id: 'darwin-option',
                    disabled: !enablePushToDarwin,
                    onSelect: () => {
                        handleMenuActions(ActionTypes.DARWIN);
                    }
                },
                ...createDeskingOption(),
                ...createRouteOneOption(),
                ...createCUDLOption()
            ].filter(Boolean),
        [showRouteOne, showCUDL, showDeskingOption, isMvLiteEnabled, enablePushToDms, enablePushToDarwin, handleMenuActions]
    );
    const shareOptions = useMemo(
        () => [
            {
                label: 'Share to Customer',
                value: ShareActionTypes.SHARE_TO_CUSTOMER,
                id: 'share-to-customer-option',
                onSelect: () => {
                    handleMenuActions(ShareActionTypes.SHARE_TO_CUSTOMER);
                    dispatch(deskingActionCreators.sendToCustomerClicked());
                }
            },
            ...(isSalesIntakeMode
                ? []
                : [
                      {
                          label: 'Share to Sales View',
                          value: ShareActionTypes.SHARE_TO_SHOW_ROOM,
                          id: 'share-to-showroom-option',
                          onSelect: () => {
                              handleMenuActions(ShareActionTypes.SHARE_TO_SHOW_ROOM);
                          }
                      }
                  ]),
            {
                label: 'Get Deal Link',
                value: ShareActionTypes.GET_DEAL_LINK,
                id: 'get-deal-link-option',
                onSelect: () => {
                    handleMenuActions(ShareActionTypes.GET_DEAL_LINK);
                }
            },
            // `onSelect` calls the hook directly rather than `handleMenuActions`; see SHARE_CREDIT_APP_LINK
            // above. Interstate fires the Menu's `onClose` after `onSelect`, so no explicit close is needed.
            ...(isConsolidatedShareMenuEnabled
                ? [
                      {
                          label: 'Share Credit App Link',
                          value: ShareActionTypes.SHARE_CREDIT_APP_LINK,
                          id: 'share-credit-app-link-option',
                          onSelect: () => shareCreditAppLink()
                      },
                      {
                          label: 'Share Doc Upload Link',
                          value: ShareActionTypes.SHARE_DOC_UPLOAD_LINK,
                          id: 'share-doc-upload-link-option',
                          onSelect: () => shareDocUploadLink()
                      }
                  ]
                : [])
        ],
        [handleMenuActions, isSalesIntakeMode, isConsolidatedShareMenuEnabled, shareCreditAppLink, shareDocUploadLink]
    );

    const actions = useMemo(() => {
        const baseActions = [
            !isBaseDealer &&
                !isFnIDealMode &&
                !isMvLiteEnabled && {
                    label: 'Offer History',
                    value: ActionTypes.OFFER_HISTORY,
                    id: 'offer-history-option',
                    onSelect: () => {
                        handleMenuActions(ActionTypes.OFFER_HISTORY);
                    }
                },
            isDealAuditLog && {
                label: 'Audit Log',
                value: ActionTypes.AUDIT_LOG,
                id: 'audit-log-option',
                onSelect: () => {
                    handleMenuActions(ActionTypes.AUDIT_LOG);
                }
            },
            !isMvLiteEnabled && {
                label: 'Begin Checkout',
                value: ActionTypes.BEGIN_CHECKOUT,
                id: 'begin-checkout-option',
                disabled: !enableBeginCheckout,
                onSelect: () => {
                    handleMenuActions(ActionTypes.BEGIN_CHECKOUT);
                }
            },
            isEnableMVCreditAppSearchAndMerge &&
                offerType !== CASH && {
                    label: 'Attach Credit App',
                    value: ActionTypes.CREDIT_APP_SEARCH,
                    id: 'credit-app-search-option',
                    onSelect: () => {
                        handleMenuActions(ActionTypes.CREDIT_APP_SEARCH);
                    },
                    disabled: isQuoteMode
                },
            !isMvLiteEnabled && {
                label: 'Open In F&I',
                value: ActionTypes.OPEN_IN_FI,
                id: 'F&I-option',
                disabled: !hasCustomerForDealerTrack,
                onSelect: () => {
                    handleMenuActions(ActionTypes.OPEN_IN_FI);
                }
            },
            !isMvLiteEnabled && {
                label: 'Open In Darwin',
                value: ActionTypes.OPEN_IN_DARWIN,
                id: 'open-in-darwin-option',
                disabled: !enablePushToDarwin,
                onSelect: () => {
                    handleMenuActions(ActionTypes.OPEN_IN_DARWIN);
                }
            },
            isGenerateContractsEnabled &&
                !isMvLiteEnabled && {
                    label: 'Generate Contract',
                    value: ActionTypes.GENERATE_CONTRACT,
                    disabled:
                        currentOfferType === BALLOON ||
                        !isGenerateContractingUiOptionEnabled ||
                        disableGenerateContractsOfferTypeChange ||
                        isQuoteMode,
                    id: 'generate-contract-option',
                    onSelect: handleGenerateContractWithCheck
                },
            showDealReview && {
                label: 'Deal Review',
                value: ActionTypes.DEAL_REVIEW,
                id: 'deal-review-option',
                onSelect: () => {
                    handleMenuActions(ActionTypes.DEAL_REVIEW);
                }
            },
            !isMvLiteEnabled && {
                label: 'Manage Documents',
                value: ActionTypes.MANAGE_DOCUMENTS,
                id: 'manage-documents-option',
                disabled: !enableDigitalDealJacket || !hasCustomerForDealerTrack,
                onSelect: () => {
                    handleMenuActions(ActionTypes.MANAGE_DOCUMENTS);
                }
            },
            isSendToDealerTrackEnabled && {
                label: 'Send to Dealertrack',
                value: ActionTypes.SEND_TO_DEALER_TRACK,
                id: 'send-to-dealer-track-option',
                disabled: isDealXgRecordMissed || !hasCustomerForDealerTrack,
                onSelect: () => {
                    handleMenuActions(ActionTypes.SEND_TO_DEALER_TRACK);
                }
            },
            (isReportIssueEnabled || isReportIssueV2Enabled) && {
                label: 'Report an Issue',
                value: ActionTypes.REPORT_AN_ISSUE,
                id: 'report-an-issue-option',
                divider: true,
                onSelect: () => {
                    handleMenuActions(ActionTypes.REPORT_AN_ISSUE);
                }
            },
            {
                label: 'Functions',
                value: ActionTypes.FUNCTIONS,
                id: 'functions-title',
                onSelect: () => {
                    handleMenuActions(ActionTypes.FUNCTIONS);
                    dispatch(interactionAnalyticsActionCreators.functionsButton());
                }
            },
            isEmployee &&
                !isBaseDealer && {
                    label: 'Manage Templates',
                    value: ActionTypes.MANAGE_TEMPLATES,
                    id: 'manage-templates',
                    onSelect: () => handleMenuActions(ActionTypes.MANAGE_TEMPLATES),
                    disabled: isQuoteMode
                },
            // Share options are added for mobile view and not available for MV Lite Mode
            ...((isDealCentralExperience && isMobileView && !isMvLiteEnabled && shareOptions) || [])
        ].flatMap((item) => (typeof item === 'boolean' || !item ? [] : [item]));

        return baseActions;
    }, [
        dealRefId,
        enableBeginCheckout,
        isEcommerceDeal,
        isGenerateContractsEnabled,
        isGenerateContractingUiOptionEnabled,
        isMobileView,
        isDealAuditLog,
        showSaveDraftButton,
        exitGate.wrap,
        shareOptions
    ]);

    const referenceProps = useMemo(
        () => ({
            open: openPushToDropdown,
            anchorEl: showPushToDropdown as MenuReferenceType,
            onClose: handlePushToButtonClose
        }),
        [openPushToDropdown, showPushToDropdown]
    );

    const menuOptions = useMemo(
        () =>
            pushToOptions.map((item) => ({
                ...item
            })),
        [pushToOptions]
    );

    const handleSavePrint = useCallback(() => {
        dispatch(printPdfActionCreator.printPdfResetSuccess());
        setPrintPreviewShown(true);
    }, [dispatch]);

    const handlePrintSettings = () => {
        const eventPayload = {
            templateId: appliedTemplateId
        };
        const analyticsEvent = createOpeningProposalSettingsEvent(eventPayload, isMvLiteEnabled);
        dispatch(analyticsEvent);
        dispatch(printPdfActionCreator.printPdfResetSuccess());
        dispatch(proposalActionCreators.toggleProposalListDrawer(true));
    };

    const handlePushToButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        setPushToDropdown(e.currentTarget);
    };

    const handleShareClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            setShareDropdown(e.currentTarget);
        },
        [setShareDropdown]
    );

    const handleActionsButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        setOpenActionsDropdown(true);
        setActionsDropdown(e.currentTarget);
    };

    const confirmDraftAndSaveRequest = () => {
        dispatch(offerActionCreators.saveOffer({ isDraft: true }));
        setShowSaveDraft(true);
    };
    const navigateToAudit = useCallback(() => {
        dispatch(navigationActionCreators.navigateToAuditLog());
    }, [dispatch]);

    if (isDealTemplatesViewEnabled && isDealCentralExperience) {
        return <TemplateButtonsBarWithBuilderMode disabled={!actionsEnabled} />;
    }
    return (
        <>
            <FailedUpdateOffer onClose={handleFailedUpdateOfferClose} show={showFailedUpdateOfferModal} />
            <UnsavedChangesModal
                show={showUnsavedChangesModal}
                errorMessage={incentiveValidationErrorMessage}
                onHide={() => setShowUnsavedChangesModal(false)}
            />
            <PushToDms ref={pushToDmsRef} isBaseScreen={true} />
            {enableDealComments && !isMvLiteEnabled && (
                <div data-testid="deal-comments-button-container">
                    <StatusIndicator indicatorContent={dealComments.showRedDot ? ('!' as any) : undefined} variant="standard">
                        <Button
                            data-testid="action-open-deal-mentions"
                            disabled={isDealXgRecordMissed || isQuoteMode}
                            buttonStyle={buttonStyle || 'secondary'}
                            size={buttonSize}
                            onClick={handleOpenDealMentions}
                            icon={<ChatBubbleLeftRightIcon />}
                        />
                    </StatusIndicator>
                    <DealComments
                        isOpen={dealComments.isOpen}
                        commentsElementRef={dealComments.commentsElementRef}
                        onClose={handleCloseDealMentions}
                        styleVariant="deal-comments-baseFooter"
                    />
                </div>
            )}
            {isLocked ? (
                <DealLockedActionsContainer>
                    {!isMvLiteEnabled && (
                        <Button
                            data-testid="manage-documents-button"
                            size={buttonSize}
                            buttonStyle={buttonStyle || 'secondary'}
                            onClick={handleManageDocumentsClick}
                            disabled={!enableDigitalDealJacket}
                        >
                            Manage Documents
                        </Button>
                    )}
                </DealLockedActionsContainer>
            ) : (
                <DropdownsContainer>
                    <Button
                        endIcon={<ChevronDownIcon />}
                        size={buttonSize}
                        buttonStyle={buttonStyle || 'secondary'}
                        disabled={!actionsEnabled}
                        className="actions-dropdown-button"
                        data-testid="action-dropdown"
                        onClick={handleActionsButtonClick}
                    >
                        Actions
                    </Button>

                    <Menu
                        size={menuSize}
                        referenceProps={{
                            open: openActionsDropdown,
                            anchorEl: actionsDropdown as MenuReferenceType,
                            onClose: handleActionsButtonClose
                        }}
                        position={'bottom-end'}
                        menuOptions={actions}
                        width={'185px'}
                    />

                    <Button
                        endIcon={<ChevronDownIcon />}
                        size={buttonSize}
                        buttonStyle={buttonStyle || 'secondary'}
                        disabled={!actionsEnabled}
                        className="push-to-dropdown-button"
                        data-testid="push-to-dropdown"
                        onClick={handlePushToButtonClick}
                    >
                        Push to...
                    </Button>
                    <Menu
                        size={menuSize}
                        referenceProps={referenceProps}
                        position={'bottom-end'}
                        menuOptions={menuOptions}
                        width={'185px'}
                    />
                    {isDealCentralExperience && !isMobileView && !isMvLiteEnabled && (
                        <>
                            <Button
                                endIcon={<ChevronDownIcon />}
                                size={buttonSize}
                                buttonStyle={buttonStyle || 'secondary'}
                                disabled={!actionsEnabled}
                                className="share-dropdown-button"
                                data-testid="share-dropdown"
                                onClick={handleShareClick}
                            >
                                Share
                            </Button>

                            <Menu
                                size={menuSize}
                                referenceProps={{
                                    open: openShareDropdown,
                                    anchorEl: showShareDropdown as MenuReferenceType,
                                    onClose: handleShareButtonClose
                                }}
                                position={'bottom-end'}
                                menuOptions={shareOptions}
                                width={largerThanSM ? '185px' : '100%'}
                            />
                        </>
                    )}
                    <SplitButton
                        data-testid="print-split-button"
                        size={(buttonSize || 'medium') as SplitButtonProps['size']}
                        buttonStyle="secondary"
                        id="print-split-button"
                        disabled={!actionsEnabled}
                        onClick={handleSavePrint}
                        options={[
                            {
                                disabled: false,
                                divider: false,
                                id: 'print-settings-option',
                                label: 'Print Settings',
                                onSelect: () => handlePrintSettings(),
                                value: 'print-settings'
                            }
                        ]}
                    >
                        Print
                    </SplitButton>

                    {!shouldShowSaveDraftBtn || isDealXgRecordMissed ? (
                        <Button
                            data-testid="update-offer-button"
                            size={buttonSize || 'medium'}
                            disabled={!actionsEnabled}
                            onClick={() => handleSaveClick()}
                        >
                            Save
                        </Button>
                    ) : (
                        <SplitButton
                            data-testid="update-offer"
                            size={(buttonSize || 'medium') as SplitButtonProps['size']}
                            id="update-offer"
                            disabled={!actionsEnabled}
                            onClick={() => handleSaveClick()}
                            options={[
                                {
                                    disabled: false,
                                    divider: false,
                                    id: '001',
                                    label: 'Save As Draft',
                                    onSelect: () => confirmDraftAndSaveRequest(),
                                    value: 'Item 1'
                                }
                            ]}
                        >
                            Save
                        </SplitButton>
                    )}
                </DropdownsContainer>
            )}
            <UpdateOfferDialog show={showUpdateOffer} onHide={updateOfferDialogOnHideCallback} />
            <UpdateOfferWithFSEventsDialog show={showUpdateOfferForFSEvents} onHide={onHideUpdateOfferForFSEvents} />
            <ShareToSalesViewDialog
                show={showShareToSalesViewDialog}
                onHide={shareToSalesViewOnHideCallback}
                onOfferSaveHide={shareToSalesViewOnOfferSaveHide}
            />
            <ShareToCustomerDialogInterstate
                prefix="share-to-customer"
                show={showShareToCustomerDialog}
                onHide={shareToCustomerDialogOnHide}
                header="Share To Customer"
                dialogPrompt="Choose how you would like to send this offer to the shopper"
            />
            <ShareToCustomerDialogInterstate
                prefix="get-deal-link"
                show={showGetDealLinkDialog}
                onHide={getToDealLinkDialogOnHide}
                header="Get Deal Link"
            />
            {isConsolidatedShareMenuEnabled && (
                <>
                    <SendToCustomerDialogInterstate {...creditAppShareLink.dialogProps} />
                    <SendToCustomerDialogInterstate {...docUploadShareLink.dialogProps} />
                </>
            )}

            <ReadyToSendDialogInterstate
                data-testid="ready-to-send-dialog"
                dialogHeader={readyToSendDialogHeader}
                dialogPrompt={readyToSendDialogPrompt}
                show={showReadyToSendDialog}
                onHide={closeReadyToSendDialog}
                onNo={closeReadyToSendDialog}
                onSaved={handleReadyToSendSaved}
                promptSx={{ padding: '0px' }}
                action={currentAction}
            />
            <PaymentRecalculatingDialog show={showPaymentRecalculatingDialog} onHide={closePaymentRecaclulatingDialog} />
            <IncompleteTradeModal show={showIncompleteTradeModal} onContinue={tradeGuard.onContinue} />
            {!showReadyToSendDialog && (
                <ReadyToSendDialogInterstate
                    data-testid="ready-to-send-save-dialog"
                    dialogHeader={'Warning'}
                    dialogPromptHeader={'Important Note:'}
                    dialogPrompt={`Pushing again will overwrite the existing deal in your DMS.`}
                    show={showReadyToSendDialogSave}
                    onHide={setShowReadyToSendDialogSaveOnNoOrHide}
                    onNo={setShowReadyToSendDialogSaveOnNoOrHide}
                    onSaved={handleSave}
                    promptSx={{
                        paddingLeft: '0',
                        b: {
                            paddingRight: '5px'
                        }
                    }}
                />
            )}
            <SnackbarWrapper>
                <Snackbar
                    data-testid="update-offer-dialog-success"
                    show={successNotification}
                    message="Deal successfully saved."
                    position="bottom-center"
                    onClose={snackbarOnCloseCallback}
                    type="success"
                />
                <Snackbar
                    data-testid="payment-calculation-done-notification"
                    show={isPaymentCalculationComplete}
                    message="Recalculation completed, you can save now"
                    position="bottom-center"
                    onClose={closePaymentNotificationSnackbar}
                    type="success"
                    autoHide
                />
            </SnackbarWrapper>
            <UpdateOfferDialog
                show={showUpdateOfferForBeginCheckout}
                onHide={() => setShowUpdateOfferForBeginCheckout(false)}
                onSuccess={() => setIsCheckoutAnywhereDrawerEnabled(true)}
            />
            {isCheckoutAnywhereDrawerEnabled && (
                <CheckoutAnywhereDrawer
                    setShowUpdateOffer={setShowUpdateOfferForBeginCheckout}
                    setIsOpenSlider={setIsCheckoutAnywhereDrawerEnabled}
                    isOpenSlider={isCheckoutAnywhereDrawerEnabled}
                />
            )}
            {!isGenericPushToR1JPath && (
                <PushToRouteOneDialog
                    data-testid="push-to-route-one-dialog"
                    show={showPushToR1Dialog}
                    onHide={handleHidePushToR1Dialog}
                />
            )}
            {isGenericPushToR1JPath && (
                <>
                    <PushToFIDialog
                        provider={ROUTE_ONE_PROVIDER_CONFIG}
                        show={openPushToFiProviderId === ROUTE_ONE_PROVIDER_CONFIG.targetPlatformId}
                        onHide={handleHidePushToFiDialog}
                    />
                    <PushToFIModal
                        provider={ROUTE_ONE_PROVIDER_CONFIG}
                        header={
                            <Typography variant="h3" sx={{ paddingTop: '1rem' }}>
                                {ROUTE_ONE_PROVIDER_CONFIG.modalCopy.title}
                            </Typography>
                        }
                    />
                </>
            )}
            <PushToFIDialog
                provider={CUDL_PROVIDER_CONFIG}
                show={openPushToFiProviderId === CUDL_PROVIDER_CONFIG.targetPlatformId}
                onHide={handleHidePushToFiDialog}
            />
            <PushToFIModal
                provider={CUDL_PROVIDER_CONFIG}
                header={
                    <Typography variant="h3" sx={{ paddingTop: '1rem' }}>
                        {CUDL_PROVIDER_CONFIG.modalCopy.title}
                    </Typography>
                }
            />
            <PrintPdfLoadingModal />
            {showFunctionsModal && <FunctionsModal onHide={() => setShowFunctionsModal(false)} />}
            <UpdateDraftDialog show={showSaveDraft} />
            <Snackbar
                data-testid="update-draft-dialog-success"
                show={showDraftSuccessSnackbar}
                message="Successfully Saved as Draft!"
                position="bottom-center"
                onClose={toggleDraftSuccessSnackbar}
                type="success"
            />
            <Snackbar
                data-testid="update-draft-dialog-failure"
                show={showDraftFailureSnackbar}
                message="Unable to Save Draft. Please Try Again"
                position="bottom-center"
                onClose={toggleDraftFailureSnackbar}
                type="error"
            />
            <Snackbar
                data-testid="das-success-snackbar"
                show={showDasSuccessSnackbar}
                message={DAS_SUCCESS_MESSAGE}
                position="bottom-center"
                onClose={dasSuccessSnackbarOnCloseCallback}
                type="success"
            />
            {isMVSalesTeamSlideoutEnabled && (
                <Snackbar
                    data-testid="update-employees-dialog-notification"
                    show={Boolean(showEmployeeUpdateNotification)}
                    message={
                        // istanbul ignore next
                        employeesHaveUpdatedSuccessFully
                            ? 'Team members have been updated successfully!'
                            : 'Unable to update team members. Please try again'
                    }
                    position="bottom-center"
                    onClose={onCloseEmployeeUpdateNotification}
                    type={
                        // istanbul ignore next
                        employeesHaveUpdatedSuccessFully ? 'success' : 'error'
                    }
                />
            )}
            <ProposalsSideOut />
            <PrintPreviewSlideOut
                show={printPreviewShown}
                header="Print Proposal Preview"
                onBack={() => setPrintPreviewShown(false)}
                onHide={() => setPrintPreviewShown(false)}
                onPublish={(selectedProposalId?, selectedScopeIds?, scopeGridSelections?) => {
                    dispatch(printPdfActionCreator.publishToShowroom(selectedProposalId, selectedScopeIds, scopeGridSelections));
                }}
                onPrint={(selectedProposalId?, isFourSquare?, selectedScopeIds?, scopeGridSelections?) => {
                    const analyticsEvent = isFourSquare
                        ? createFourSquarePrintLayoutSelectedEvent(
                              { proposalId: selectedProposalId, templateId: appliedTemplateId },
                              isMvLiteEnabled
                          )
                        : createPrintingSelectedProposalEvent({ templateId: appliedTemplateId }, isMvLiteEnabled);
                    dispatch(analyticsEvent);
                    dispatch(printPdfActionCreator.printCustomizablePdf(selectedProposalId, selectedScopeIds, scopeGridSelections));
                }}
            />
            {!isReportIssueV2Enabled && (
                <ReportIssueSlideOut show={showReportIssueSlideout} onHide={() => setShowReportIssueSlideout(false)} />
            )}
            {isReportIssueV2Enabled && (
                <ReportIssueSlideOutV2 show={showReportIssueSlideout} onHide={() => setShowReportIssueSlideout(false)} />
            )}
            <ContractFormTypeModal
                show={showContractFormTypeModal}
                onConfirm={handleConfirmContractFormType}
                onClose={handleCloseContractFormTypeModal}
            />
        </>
    );
};

export default ActionsInterstate;
